import { useState } from "react";
import bcrypt from 'bcryptjs';
import { supabase } from '../utils/supabaseClient';

export function usePasswordChange({ showSavedToast }) {
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const [passwordData, setPasswordData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);

  const handleChangePassword = () => {
    setShowPasswordFields(true);
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSavePassword = async () => {
    setPasswordError('');
    if (!passwordData.oldPassword || !passwordData.newPassword) {
      setPasswordError('Fill in both your current and new password.');
      return;
    }
    if (passwordData.newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters.');
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match.');
      return;
    }
    setPasswordSaving(true);
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser?.email) throw new Error('Not authenticated');

      // Verify the current password by re-authenticating. Supabase
      // updateUser({password}) doesn't ask for the old password itself,
      // so we have to gate the change manually.
      const { error: signInErr } = await supabase.auth.signInWithPassword({
        email: authUser.email,
        password: passwordData.oldPassword,
      });
      if (signInErr) {
        setPasswordError('Current password is incorrect.');
        return;
      }

      const { error: updErr } = await supabase.auth.updateUser({
        password: passwordData.newPassword,
      });
      if (updErr) throw updErr;

      // Mirror to legacy teachers.password_hash so old code paths that
      // still read it stay in sync (it's NOT NULL on the table).
      try {
        const passwordHash = await bcrypt.hash(passwordData.newPassword, 10);
        await supabase
          .from('teachers')
          .update({ password_hash: passwordHash })
          .eq('auth_user_id', authUser.id);
      } catch (mirrorErr) {
        console.warn('teachers.password_hash mirror failed:', mirrorErr?.message);
      }

      setShowPasswordFields(false);
      setPasswordData({ oldPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordError('');
      showSavedToast();
    } catch (e) {
      console.error('Password change error:', e);
      setPasswordError(e?.message || 'Could not change password. Try again.');
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleCancelPassword = () => {
    setShowPasswordFields(false);
    setPasswordData({
      oldPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setPasswordError('');
  };

  return {
    showPasswordFields,
    passwordData,
    passwordError,
    passwordSaving,
    handleChangePassword, handlePasswordChange, handleSavePassword, handleCancelPassword,
  };
}
